import React, { useState, useEffect, useCallback } from 'react';
import { commissionAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, Users, Plus, Trash2, Loader2, Award, Percent } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function CommissionDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await commissionAPI.getDashboard();
      setData(res.data);
    } catch { toast.error('Failed to load commissions'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const s = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="commission-page">
      <div><h1 className="text-2xl font-bold tracking-tight">Commission Tracking</h1>
        <p className="text-sm text-muted-foreground mt-1">Sales compensation and earnings dashboard</p></div>

      <Tabs defaultValue="dashboard">
        <TabsList><TabsTrigger value="dashboard" data-testid="tab-commission-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-commission-rules">Commission Rules</TabsTrigger></TabsList>

        <TabsContent value="dashboard" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard icon={DollarSign} label="Total Commission" value={`$${(s.total_commission || 0).toLocaleString()}`} color="text-emerald-500" />
            <MetricCard icon={TrendingUp} label="Total Revenue" value={`$${(s.total_revenue || 0).toLocaleString()}`} color="text-blue-500" />
            <MetricCard icon={Users} label="Reps" value={s.total_reps || 0} color="text-purple-500" />
            <MetricCard icon={Percent} label="Avg Rate" value={`${s.avg_commission_rate || 0}%`} color="text-amber-500" />
          </div>

          {(data?.reps || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Commission by Rep</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.reps}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                    <XAxis dataKey="rep_name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={v => `$${v.toLocaleString()}`} />
                    <Bar dataKey="total_commission" fill="#10b981" name="Commission" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="total_revenue" fill="#3b82f6" name="Revenue" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="text-sm">Rep Earnings Detail</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(data?.reps || []).map((r, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50" data-testid={`rep-row-${i}`}>
                    <div className="flex items-center gap-3">
                      <Award className="h-5 w-5 text-amber-500" />
                      <div><p className="text-sm font-medium">{r.rep_name}</p>
                        <p className="text-xs text-muted-foreground">{r.deals_closed} deals closed</p></div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-emerald-500">${r.total_commission.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">from ${r.total_revenue.toLocaleString()} revenue</p>
                    </div>
                  </div>
                ))}
                {(data?.reps || []).length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No closed deals with commissions yet</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules" className="mt-4"><CommissionRulesTab onUpdate={load} /></TabsContent>
      </Tabs>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, color }) {
  return (
    <Card><CardContent className="pt-4 pb-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10"><Icon className={`h-4 w-4 ${color}`} /></div>
        <div><p className="text-2xl font-bold">{value}</p><p className="text-xs text-muted-foreground">{label}</p></div>
      </div>
    </CardContent></Card>
  );
}

function CommissionRulesTab({ onUpdate }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'percentage', rate: 10 });

  useEffect(() => {
    (async () => {
      try { const r = await commissionAPI.getRules(); setRules(r.data.rules || []); } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const handleCreate = async () => {
    if (!form.name) return toast.error('Name required');
    try {
      await commissionAPI.createRule({ ...form, rate: parseFloat(form.rate) });
      toast.success('Rule created');
      setShowForm(false);
      const r = await commissionAPI.getRules(); setRules(r.data.rules || []);
      onUpdate();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try { await commissionAPI.deleteRule(id); toast.success('Deleted');
      const r = await commissionAPI.getRules(); setRules(r.data.rules || []); onUpdate(); } catch {}
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-3" data-testid="commission-rules">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="add-commission-rule"><Plus className="h-3.5 w-3.5 mr-1" />Add Rule</Button>
      </div>
      {showForm && (
        <Card><CardContent className="pt-4 pb-4 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Input placeholder="Rule name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="commission-rule-name" />
            <select className="border rounded-md px-3 py-2 text-sm bg-background" value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
              <option value="percentage">Percentage</option><option value="tiered">Tiered</option><option value="flat">Flat Amount</option>
            </select>
            <Input type="number" placeholder="Rate %" value={form.rate} onChange={e => setForm({...form, rate: e.target.value})} data-testid="commission-rate" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={handleCreate} data-testid="save-commission-rule">Save</Button>
          </div>
        </CardContent></Card>
      )}
      {rules.map(r => (
        <Card key={r.id}><CardContent className="pt-3 pb-3 flex items-center justify-between">
          <div><p className="text-sm font-medium">{r.name}</p>
            <p className="text-xs text-muted-foreground">{r.type}: {r.rate}%</p></div>
          <div className="flex gap-2">
            <Badge variant={r.is_active ? 'default' : 'secondary'}>{r.is_active ? 'Active' : 'Inactive'}</Badge>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(r.id)}><Trash2 className="h-3 w-3" /></Button>
          </div>
        </CardContent></Card>
      ))}
      {rules.length === 0 && !showForm && <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No commission rules. Default 10% applies.</CardContent></Card>}
    </div>
  );
}
